import { useEffect, useRef } from "react";
import { ApiError, post } from "../../api/client.ts";
import type { BrandResponse, BriefSubmissionResponse } from "../../api/types.ts";
import { useBrand } from "../../state/BrandContext.tsx";

export function showContentOffer(brand: BrandResponse | null, submission: BriefSubmissionResponse | null): boolean {
  return !!brand && !!submission && submission.status === "ready" && submission.contentSourcingFeeCents > 0;
}

/** The quote points to setup. Folder input lives only in the setup checklist. */
export function ContentFolderOffer({ brand, submission, onQuoteChange, onAddContent, contentRevision = 0 }: {
  brand: BrandResponse;
  submission: BriefSubmissionResponse | null;
  onQuoteChange?: (next: BriefSubmissionResponse) => void;
  onAddContent: () => void;
  contentRevision?: number;
}) {
  const { notify } = useBrand();
  const seenRevision = useRef(contentRevision);
  const quoteId = submission?.id;
  const canReprice = showContentOffer(brand, submission);
  useEffect(() => {
    if (seenRevision.current === contentRevision) return;
    seenRevision.current = contentRevision;
    if (!quoteId || !canReprice) return;
    let live = true;
    void post<{ submission: BriefSubmissionResponse }>(`/api/brief-submissions/${quoteId}/reprice-content`)
      .then(result => {
        if (!live) return;
        onQuoteChange?.(result.submission);
        notify(result.submission.contentSourcingFeeCents > 0
          ? "Content saved. It doesn't change this quote yet."
          : "Your updated quote is ready. Review it before approving.", "success");
      })
      .catch(err => {
        if (!live) return;
        const body = err instanceof ApiError ? err.body as { code?: string; submission?: BriefSubmissionResponse } | null : null;
        if (body?.submission) onQuoteChange?.(body.submission);
        notify(body?.code === "unchanged"
          ? "Content saved. Your quote is unchanged."
          : "Content saved, but we couldn't refresh the quote. Review the price before approving.", "error");
      });
    return () => { live = false; };
  }, [contentRevision, brand.id, quoteId, canReprice, onQuoteChange, notify]);

  if (!showContentOffer(brand, submission)) return null;
  return (
    <div className="brief-content-offer" role="group" aria-label="Content savings">
      <p className="brief-content-offer-line">
        Save on Fare Prices by Providing Content!{" "}
        {(brand.role ?? "owner") === "owner"
          ? <button type="button" className="brief-content-add" onClick={onAddContent}>Add Content</button>
          : <span className="brief-content-owner">Ask your brand owner to add content.</span>}
      </p>
      <p className="brief-content-description">Usable footage can lower your quote. Sorting new content may take about a day. Approving now uses the price shown above.</p>
    </div>
  );
}
